import { useEffect, useMemo, useState } from "react";
import { fetchHomepageSections, updateHomepageSections } from "../api";
import {
    buildManualSectionBooks,
    normalizeHomepageSections,
    SPECIAL_SECTION_KEYS,
} from "../homepageSections";

export default function AdminHomepageSectionManager({ user, books = [] }) {
    const [sections, setSections] = useState({});
    const [orderedKeys, setOrderedKeys] = useState([]);
    const [searches, setSearches] = useState({});
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState("");
    const [error, setError] = useState("");
    const [newSectionLabel, setNewSectionLabel] = useState("");

    useEffect(() => {
        let isMounted = true;

        const loadSections = async () => {
            setLoading(true);
            setError("");

            try {
                const data = await fetchHomepageSections();
                if (isMounted) {
                    const normalized = normalizeHomepageSections(data.sections);
                    setSections(normalized);
                    const sorted = Object.values(normalized)
                        .sort((a, b) => a.sortOrder - b.sortOrder)
                        .map(s => s.sectionKey);
                    setOrderedKeys(sorted);
                }
            } catch (err) {
                if (isMounted) {
                    setError(err.message || "Failed to load homepage section settings");
                }
            } finally {
                if (isMounted) {
                    setLoading(false);
                }
            }
        };

        loadSections();

        return () => {
            isMounted = false;
        };
    }, []);

    const booksById = useMemo(() => {
        return new Map(books.map((book) => [String(book?._id ?? book?.id ?? ""), book]));
    }, [books]);

    const updateSection = (sectionKey, updater) => {
        setSections((current) => {
            const currentSection = current[sectionKey];
            if (!currentSection) return current;
            const nextValue = typeof updater === "function" ? updater(currentSection) : updater;

            return {
                ...current,
                [sectionKey]: {
                    ...currentSection,
                    ...nextValue,
                },
            };
        });
        setMessage("");
    };

    const handleLimitChange = (sectionKey, value) => {
        const parsed = Number(value);
        updateSection(sectionKey, {
            itemLimit: Number.isFinite(parsed) && parsed > 0 ? Math.floor(parsed) : 1,
        });
    };

    const handleToggleBook = (sectionKey, bookId) => {
        updateSection(sectionKey, (currentSection) => {
            const normalizedBookId = String(bookId);
            const alreadySelected = currentSection.bookIds.includes(normalizedBookId);

            return {
                bookIds: alreadySelected
                    ? currentSection.bookIds.filter((id) => id !== normalizedBookId)
                    : [...currentSection.bookIds, normalizedBookId],
            };
        });
    };

    const handleToggleActive = (sectionKey) => {
        updateSection(sectionKey, (currentSection) => ({
            isActive: !currentSection.isActive
        }));
    };

    const handleAddSection = () => {
        if (!newSectionLabel.trim()) return;
        const key = "sec_" + Date.now() + "_" + Math.floor(Math.random() * 1000);
        const newSec = {
            sectionKey: key,
            label: newSectionLabel.trim(),
            description: "",
            sortOrder: orderedKeys.length,
            itemLimit: 8,
            bookIds: [],
            isActive: true,
        };
        setSections(s => ({ ...s, [key]: newSec }));
        setOrderedKeys(keys => [...keys, key]);
        setNewSectionLabel("");
        setMessage("Section added! Don't forget to save changes.");
    };

    const handleDeleteSection = (sectionKey) => {
        if (SPECIAL_SECTION_KEYS.includes(sectionKey)) {
            alert("This is a special built-in section and cannot be deleted.");
            return;
        }
        if (!window.confirm("Are you sure you want to delete this section?")) return;

        setSections(s => {
            const copy = { ...s };
            delete copy[sectionKey];
            return copy;
        });
        setOrderedKeys(keys => keys.filter(k => k !== sectionKey));
        setMessage("Section removed. Click save to persist.");
    };

    // Drag and Drop
    const handleDragStart = (e, index) => {
        e.dataTransfer.setData("dragIndex", index);
        e.currentTarget.style.opacity = "0.4";
    };

    const handleDragEnd = (e) => {
        e.currentTarget.style.opacity = "1";
    };

    const handleDragOver = (e) => {
        e.preventDefault();
    };

    const handleDrop = (e, dropIndex) => {
        e.preventDefault();
        const dragIndex = Number(e.dataTransfer.getData("dragIndex"));
        if (dragIndex === dropIndex) return;

        const newKeys = [...orderedKeys];
        const draggedKey = newKeys[dragIndex];
        newKeys.splice(dragIndex, 1);
        newKeys.splice(dropIndex, 0, draggedKey);

        setOrderedKeys(newKeys);
        
        // Update sortOrders immediately in local state
        setSections(current => {
            const updated = { ...current };
            newKeys.forEach((key, index) => {
                if (updated[key]) {
                    updated[key].sortOrder = index;
                }
            });
            return updated;
        });
        setMessage("Reordered! Click save to persist.");
    };

    const handleSave = async () => {
        setSaving(true);
        setError("");
        setMessage("");

        try {
            // Apply current sort order based on orderedKeys
            const finalSections = { ...sections };
            orderedKeys.forEach((key, i) => {
                if (finalSections[key]) {
                    finalSections[key].sortOrder = i;
                }
            });

            const data = await updateHomepageSections(user.token, finalSections);
            const normalized = normalizeHomepageSections(data.sections);
            setSections(normalized);
            const sorted = Object.values(normalized)
                .sort((a, b) => a.sortOrder - b.sortOrder)
                .map(s => s.sectionKey);
            setOrderedKeys(sorted);
            setMessage("Homepage section settings saved.");
        } catch (err) {
            setError(err.message || "Failed to save homepage section settings");
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div style={{ textAlign: "center", padding: "60px 20px", color: "var(--text-light)" }}>
                Loading homepage section settings...
            </div>
        );
    }

    return (
        <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
            <div style={{
                background: "white",
                border: "1px solid var(--border)",
                borderRadius: 16,
                padding: 20,
                display: "flex",
                justifyContent: "space-between",
                gap: 16,
                flexWrap: "wrap",
                alignItems: "center",
            }}>
                <div>
                    <h2 style={{ margin: "0 0 6px 0", fontSize: "1.4rem" }}>Homepage Sections</h2>
                    <p style={{ margin: 0, color: "var(--text-light)" }}>
                        Create dynamic book carousels, reorder them, and pick the books you want to show.
                    </p>
                </div>
                <button
                    className="btn btn-forest"
                    onClick={handleSave}
                    disabled={saving}
                    style={{ minWidth: 150, justifyContent: "center", opacity: saving ? 0.7 : 1 }}
                >
                    {saving ? "Saving..." : "Save Changes"}
                </button>
            </div>

            {/* Add New Section */}
            <div style={{
                background: "white",
                border: "1px dashed var(--border)",
                borderRadius: 16,
                padding: 20,
                display: "flex",
                gap: 12,
                alignItems: "center"
            }}>
                <input
                    type="text"
                    value={newSectionLabel}
                    onChange={e => setNewSectionLabel(e.target.value)}
                    placeholder="Enter new section name (e.g. Just Released)"
                    style={{
                        flex: 1,
                        padding: "12px 14px",
                        borderRadius: 10,
                        border: "1px solid var(--border)",
                        fontFamily: "inherit",
                    }}
                />
                <button 
                    onClick={handleAddSection} 
                    className="btn btn-gold"
                    style={{ flexShrink: 0 }}
                >
                    + Add New Section
                </button>
            </div>

            {error && (
                <div style={{
                    background: "#fff4f4",
                    border: "1px solid #f3b5b5",
                    color: "#8b1e1e",
                    borderRadius: 12,
                    padding: "12px 16px",
                }}>
                    {error}
                </div>
            )}

            {message && (
                <div style={{
                    background: "#eef9f0",
                    border: "1px solid #b9dfc0",
                    color: "#235f2f",
                    borderRadius: 12,
                    padding: "12px 16px",
                }}>
                    {message}
                </div>
            )}

            {orderedKeys.map((sectionKey, index) => {
                const section = sections[sectionKey];
                if (!section) return null;

                const isSpecial = SPECIAL_SECTION_KEYS.includes(sectionKey);
                const selectedBooks = buildManualSectionBooks(books, section);
                const selectedIdSet = new Set(section.bookIds);
                const searchValue = searches[sectionKey] || "";
                const filteredBooks = books.filter((book) => {
                    const haystack = `${book.title || ""} ${book.author || ""}`.toLowerCase();
                    return haystack.includes(searchValue.toLowerCase());
                });

                return (
                    <div
                        key={sectionKey}
                        draggable
                        onDragStart={(e) => handleDragStart(e, index)}
                        onDragEnd={handleDragEnd}
                        onDragOver={handleDragOver}
                        onDrop={(e) => handleDrop(e, index)}
                        style={{
                            background: "white",
                            border: "1px solid var(--border)",
                            borderRadius: 16,
                            padding: 20,
                            display: "flex",
                            flexDirection: "column",
                            gap: 18,
                            cursor: "move",
                            boxShadow: "0 2px 8px rgba(0,0,0,0.02)"
                        }}
                    >
                        <div style={{ display: "flex", justifyContent: "space-between", gap: 16, flexWrap: "wrap", alignItems: "flex-start" }}>
                            <div style={{ display: "flex", alignItems: "flex-start", gap: 12, flex: 1 }}>
                                <div style={{ paddingTop: 4, cursor: "grab", color: "#ccc", fontSize: 20 }}>
                                    ⋮⋮
                                </div>
                                <label style={{ display: "flex", alignItems: "center", cursor: "pointer", gap: 8, marginTop: 4 }}>
                                    <input 
                                        type="checkbox" 
                                        checked={section.isActive} 
                                        onChange={() => handleToggleActive(sectionKey)}
                                        style={{ width: 18, height: 18, cursor: "pointer" }}
                                    />
                                </label>
                                <div style={{ flex: 1 }}>
                                    {isSpecial ? (
                                        <h3 style={{ margin: "0 0 4px 0", fontSize: "1.15rem", opacity: section.isActive ? 1 : 0.5 }}>{section.label} <span style={{fontSize: 10, background:"#f0f0f0", padding:"2px 6px", borderRadius:4}}>Built-in UI</span></h3>
                                    ) : (
                                        <input
                                            type="text"
                                            value={section.label}
                                            onChange={e => updateSection(sectionKey, { label: e.target.value })}
                                            style={{
                                                fontSize: "1.15rem",
                                                fontWeight: 700,
                                                border: "1px dashed var(--border)",
                                                padding: "4px 8px",
                                                borderRadius: 6,
                                                marginBottom: 4,
                                                width: "100%",
                                                fontFamily: "inherit",
                                                opacity: section.isActive ? 1 : 0.5
                                            }}
                                            onClick={e => e.stopPropagation()}
                                        />
                                    )}
                                    <input
                                        type="text"
                                        placeholder="Optional description"
                                        value={section.description}
                                        onChange={e => updateSection(sectionKey, { description: e.target.value })}
                                        style={{
                                            fontSize: "0.92rem",
                                            border: "1px dashed transparent",
                                            padding: "2px 4px",
                                            borderRadius: 4,
                                            width: "100%",
                                            fontFamily: "inherit",
                                            color: "var(--text-light)",
                                            opacity: section.isActive ? 1 : 0.5
                                        }}
                                        onFocus={e => e.currentTarget.style.borderColor = "var(--border)"}
                                        onBlur={e => e.currentTarget.style.borderColor = "transparent"}
                                        onClick={e => e.stopPropagation()}
                                    />
                                </div>
                            </div>

                            <div style={{ display: "flex", gap: 16, alignItems: "flex-start" }}>
                                {sectionKey !== "authors" && (
                                    <label style={{ display: "flex", flexDirection: "column", gap: 6, minWidth: 100 }}>
                                        <span style={{ fontSize: "0.85rem", fontWeight: 700, color: "var(--text-light)" }}>
                                            Item Limit
                                        </span>
                                        <input
                                            type="number"
                                            min="1"
                                            max="24"
                                            value={section.itemLimit}
                                            onChange={(event) => handleLimitChange(sectionKey, event.target.value)}
                                            style={{
                                                padding: "8px 12px",
                                                borderRadius: 10,
                                                border: "1px solid var(--border)",
                                                fontFamily: "inherit",
                                            }}
                                            onClick={e => e.stopPropagation()}
                                        />
                                    </label>
                                )}
                                {!isSpecial && (
                                    <button
                                        onClick={(e) => { e.stopPropagation(); handleDeleteSection(sectionKey); }}
                                        style={{
                                            padding: "8px 12px",
                                            marginTop: 26,
                                            background: "#fff4f4",
                                            color: "#8b1e1e",
                                            border: "1px solid #f3b5b5",
                                            borderRadius: 8,
                                            cursor: "pointer",
                                            fontWeight: 600,
                                            fontSize: "0.85rem"
                                        }}
                                    >
                                        Delete
                                    </button>
                                )}
                            </div>
                        </div>

                        {sectionKey !== "authors" && sectionKey !== "category" && (
                            <>
                                <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 10 }}>
                                    <div style={{ fontSize: "0.85rem", fontWeight: 700, color: "var(--text-light)" }}>
                                        Selected Books ({section.bookIds.length})
                                    </div>

                                    {selectedBooks.length === 0 ? (
                                        <div style={{
                                            padding: "14px 16px",
                                            borderRadius: 12,
                                            background: "#fcf8f8",
                                            color: "var(--text-light)",
                                        }}>
                                            <p style={{ margin: "0 0 10px 0" }}>No manual books selected yet. The homepage is currently automatically displaying these books:</p>
                                            <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
                                                {books.slice(0, section.itemLimit).map((book, i) => (
                                                    <div
                                                        key={book._id || i}
                                                        style={{
                                                            border: "1px dashed var(--border)",
                                                            background: "white",
                                                            color: "var(--text-light)",
                                                            borderRadius: 999,
                                                            padding: "6px 12px",
                                                            fontSize: "0.85rem"
                                                        }}
                                                    >
                                                        {i + 1}. {book.title}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    ) : (
                                        <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
                                            {section.bookIds.map((bookId, i) => {
                                                const book = booksById.get(String(bookId));
                                                if (!book) return null;

                                                return (
                                                    <button
                                                        key={bookId}
                                                        onClick={(e) => { e.stopPropagation(); handleToggleBook(sectionKey, bookId); }}
                                                        style={{
                                                            border: "1px solid #e8c9c9",
                                                            background: "#fff8f8",
                                                            color: "#8B1A1A",
                                                            borderRadius: 999,
                                                            padding: "8px 14px",
                                                            display: "flex",
                                                            gap: 8,
                                                            alignItems: "center",
                                                            cursor: "pointer",
                                                            fontFamily: "inherit",
                                                        }}
                                                        title="Remove from this section"
                                                    >
                                                        <span style={{ fontWeight: 700 }}>{i + 1}.</span>
                                                        <span>{book.title}</span>
                                                        <span style={{ opacity: 0.6 }}>x</span>
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>

                                <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                                    <input
                                        type="text"
                                        placeholder={`Search books to add...`}
                                        value={searchValue}
                                        onClick={e => e.stopPropagation()}
                                        onChange={(event) => setSearches((current) => ({
                                            ...current,
                                            [sectionKey]: event.target.value,
                                        }))}
                                        style={{
                                            padding: "12px 14px",
                                            borderRadius: 10,
                                            border: "1px solid var(--border)",
                                            fontFamily: "inherit",
                                        }}
                                    />

                                    <div style={{
                                        border: "1px solid #efe5d8",
                                        borderRadius: 14,
                                        maxHeight: 200,
                                        overflowY: "auto",
                                    }}>
                                        {filteredBooks.length === 0 ? (
                                            <div style={{ padding: 16, color: "var(--text-light)" }}>
                                                No books match this search.
                                            </div>
                                        ) : (
                                            filteredBooks.map((book) => {
                                                const selected = selectedIdSet.has(String(book._id));

                                                return (
                                                    <label
                                                        key={book._id}
                                                        onClick={e => e.stopPropagation()}
                                                        style={{
                                                            display: "flex",
                                                            alignItems: "center",
                                                            gap: 12,
                                                            padding: "10px 14px",
                                                            borderBottom: "1px solid #f4ece2",
                                                            background: selected ? "#fff7f1" : "white",
                                                            cursor: "pointer",
                                                        }}
                                                    >
                                                        <input
                                                            type="checkbox"
                                                            checked={selected}
                                                            onChange={() => handleToggleBook(sectionKey, book._id)}
                                                        />
                                                        <div style={{ flex: 1, minWidth: 0 }}>
                                                            <div style={{ fontWeight: 700, color: "var(--ink)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{book.title}</div>
                                                            <div style={{ fontSize: "0.85rem", color: "var(--text-light)" }}>
                                                                {book.author || "Unknown author"}
                                                            </div>
                                                        </div>
                                                    </label>
                                                );
                                            })
                                        )}
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                );
            })}
        </div>
    );
}
